import type { Publication } from '~/interfaces/publication';

const PROPERTIES = [
  'og:title',
  'og:url',
  'og:description',
  'og:description',
  'og:image',
  'og:title',
  'og:image:width',
  'og:image:height',
  'og:image:alt',
  'og:type',
  'og:site_name',
];

const NAMES = [
  'keywords',
  'author',
  'robots',
  'viewport',
  'description',
  'twitter:title',
  'twitter:url',
  'twitter:description',
  'twitter:image',
  'twitter:card',
  'twitter:creator',
  'fb:app_id',
];

type AllowedOverrideKeys = (typeof PROPERTIES)[number] | (typeof NAMES)[number];

interface AdditionalLink {
  tagName: string;
  rel: string;
  href: string;
  as: string;
}

interface RootMetaItem {
  [key: string]: string;
}

interface GetMergedMetaDataOptions {
  canonicalUrl?: string;
  matches: any[];
  overrides?: Partial<Record<AllowedOverrideKeys, string>>;
  links?: AdditionalLink[];
  returnEarlyCheck?: boolean;
  structuredData?: any;
}

const getMergedMetaData = ({
  canonicalUrl,
  matches,
  overrides = {},
  links = [],
  returnEarlyCheck = false,
  structuredData,
}: GetMergedMetaDataOptions) => {
  const root = matches.find((match: any) => match?.id === 'root');
  const publication: Publication | undefined = root?.data?.publication;
  const title = overrides?.title || publication?.name || '';
  const description = overrides?.description || publication?.description || '';

  const twitterTitle = overrides?.['twitter:title'] || title;
  const twitterDescription = overrides?.['twitter:description'] || description;
  const twitterImage = overrides?.['twitter:image'] || publication?.logo?.thumb?.url || '';
  const twitterCreator = overrides?.['twitter:creator'] || '';
  const twitterUrl = overrides?.['twitter:url'] || publication?.url || '';

  const facebookTitle = overrides?.['og:title'] || title;
  const facebookDescription = overrides?.['og:description'] || description;
  const facebookImage = overrides?.['og:image'] || publication?.logo?.thumb?.url || '';
  const facebookUrl = overrides?.['og:url'] || publication?.url || '';

  // Meta data in child routes will override their parent's meta data
  // This insures we merged in the relevant meta data from the root route
  const rootMeta: RootMetaItem[] | undefined = root?.meta;
  const dynamicLinks: RootMetaItem[] = [];
  const rootMetaMerged: RootMetaItem =
    rootMeta?.reduce((acc, obj) => {
      if (obj.tagName === 'link') {
        dynamicLinks.push(obj);
        return acc;
      }

      const key = Object.keys(obj)[0];
      const value = obj[key];
      acc[key] = value;
      return acc;
    }, {}) || {};

  if (returnEarlyCheck || !publication) {
    return [];
  }

  const metaData: RootMetaItem = {
    ...rootMetaMerged,
    title: title,
    description: description,
    'og:type': 'website',
    'og:url': facebookUrl,
    'og:title': facebookTitle,
    'og:description': facebookDescription,
    'og:site_name': publication.name,
    'og:image': facebookImage,
    'og:image:alt': facebookDescription,
    'twitter:url': twitterUrl,
    'twitter:title': twitterTitle,
    'twitter:description': twitterDescription,
    'twitter:image': twitterImage,
    'twitter:card': 'summary_large_image',
    'twitter:creator': twitterCreator,
    'fb:app_id': `${root?.data?.facebookAppId}`,
    ...overrides,
  };

  const metaDataArray: Array<any> = Object.keys(metaData).map((key) => {
    return { [key]: metaData[key] };
  });

  let restructuredArray: Array<any> = metaDataArray.map((item) => {
    const key = Object.keys(item)[0];
    if (PROPERTIES.includes(key)) {
      return {
        property: key,
        content: item[key],
      };
    }

    if (NAMES.includes(key)) {
      return {
        name: key,
        content: item[key],
      };
    }

    return item;
  });

  // Add canonical link if it exists
  if (canonicalUrl) {
    restructuredArray.push({
      tagName: 'link',
      rel: 'canonical',
      href: canonicalUrl,
    });
  }

  // Merge in dynamic links
  restructuredArray.push(...dynamicLinks);
  if (links.length > 0) {
    restructuredArray.push(...links);
  }

  // Add structured data if it exists
  if (structuredData) {
    restructuredArray.push({
      'script:ld+json': {
        ...structuredData,
      },
    });
  }

  // create a set of unique meta tags where key is JSON.stringify of meta
  const uniqueMeta = new Set<string>(restructuredArray.map((item) => JSON.stringify(item)));

  restructuredArray = Array.from(uniqueMeta).map((item) => JSON.parse(item));

  return restructuredArray;
};

export default getMergedMetaData;
